import { useSyncExternalStore } from 'react';
import type { AnalyticsEventName, AnalyticsProduct, Locale } from '@cfg/shared';
import { Button } from './components';

/**
 * Consent-based, first-party usage analytics. Nothing is stored or sent before the visitor
 * accepts; declining is remembered. Events are batched and delivered with `sendBeacon` so they
 * survive page unloads. The visitor id is random and never linked to personal data.
 */

export type Consent = 'granted' | 'denied' | null;
type Props = Record<string, string | number | boolean | null>;

const CONSENT_KEY = 'cfg-consent';
const VISITOR_KEY = 'cfg-visitor';

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export interface Analytics {
  track(name: AnalyticsEventName, props?: Props): void;
  consent(): Consent;
  setConsent(value: Exclude<Consent, null>): void;
  subscribe(listener: () => void): () => void;
  flush(): void;
}

export function createAnalytics(options: {
  apiUrl: string;
  product: AnalyticsProduct;
  flushMs?: number;
}): Analytics {
  const endpoint = `${options.apiUrl.replace(/\/$/, '')}/v1/events`;
  const listeners = new Set<() => void>();
  let queue: { name: AnalyticsEventName; props: Props; at: number }[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let memoryConsent: Consent = null;

  const read = (): Consent => {
    const value = storage()?.getItem(CONSENT_KEY) ?? memoryConsent;
    return value === 'granted' || value === 'denied' ? value : null;
  };

  const visitor = (): string => {
    const store = storage();
    let id = store?.getItem(VISITOR_KEY) ?? null;
    if (!id) {
      id = (
        crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
      ).replace(/[^A-Za-z0-9_-]/g, '');
      store?.setItem(VISITOR_KEY, id);
    }
    return id;
  };

  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!queue.length || read() !== 'granted') return;
    const body = JSON.stringify({
      product: options.product,
      visitor: visitor(),
      events: queue.splice(0, 50),
    });
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
    const sent = typeof navigator !== 'undefined' && navigator.sendBeacon?.(endpoint, blob);
    if (!sent)
      void fetch(endpoint, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'content-type': 'text/plain' },
      }).catch(() => undefined);
    if (queue.length) flush();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  return {
    track(name, props = {}) {
      if (read() !== 'granted') return;
      queue.push({ name, props, at: Date.now() });
      if (queue.length >= 20) flush();
      else if (!timer) timer = setTimeout(flush, options.flushMs ?? 3000);
    },
    consent: read,
    setConsent(value) {
      memoryConsent = value;
      const store = storage();
      try {
        store?.setItem(CONSENT_KEY, value);
        if (value === 'denied') store?.removeItem(VISITOR_KEY);
      } catch {
        // storage unavailable: keep the choice for this page only
      }
      if (value === 'denied') queue = [];
      listeners.forEach((l) => l());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    flush,
  };
}

export function useConsent(analytics: Analytics): Consent {
  return useSyncExternalStore(analytics.subscribe, analytics.consent, () => 'denied');
}

const TEXT = {
  en: {
    title: 'Help us improve',
    body: 'We would like to count how the configurator is used (for example which materials are popular). No personal details are collected.',
    accept: 'Allow',
    decline: 'No thanks',
    privacy: 'Privacy policy',
  },
  he: {
    title: 'עזרו לנו להשתפר',
    body: 'נשמח לספור כיצד נעשה שימוש בהדמיה (למשל אילו חומרים פופולריים). לא נאסף מידע אישי.',
    accept: 'אישור',
    decline: 'לא, תודה',
    privacy: 'מדיניות פרטיות',
  },
} as const;

/** Small, non-blocking consent banner. Renders nothing once the visitor has chosen. */
export function ConsentBanner({
  analytics,
  locale,
  privacyUrl,
}: {
  analytics: Analytics;
  locale: Locale;
  privacyUrl?: string;
}) {
  const consent = useConsent(analytics);
  if (consent !== null) return null;
  const t = TEXT[locale];
  return (
    <section
      className="cfg-consent"
      role="region"
      aria-label={t.title}
      dir={locale === 'he' ? 'rtl' : 'ltr'}
    >
      <div className="cfg-consent__text">
        <strong>{t.title}</strong>
        <span>
          {t.body}{' '}
          {privacyUrl && (
            <a href={privacyUrl} target="_blank" rel="noreferrer">
              {t.privacy}
            </a>
          )}
        </span>
      </div>
      <div className="cfg-consent__actions">
        <Button size="sm" variant="ghost" onClick={() => analytics.setConsent('denied')}>
          {t.decline}
        </Button>
        <Button size="sm" variant="primary" onClick={() => analytics.setConsent('granted')}>
          {t.accept}
        </Button>
      </div>
    </section>
  );
}
